/**
 * Wizard de cadastro inicial — derivado de `Lumma Fit Criar Conta.dc.html`.
 *
 * O protótipo tinha 6 passos. Aqui são 3: dados da loja, acesso e revisão.
 * Grade de tamanhos, primeiro produto e equipe saíram do cadastro e passam a
 * ser telas do sistema — o caminho até "a loja está criada" fica curto, que é
 * o que decide se a pessoa termina o cadastro.
 *
 * A loja continua nascendo utilizável: sem grade informada, o backend aplica a
 * grade padrão (P/M/G/GG e 38–42), ajustável em Configurações. Uma loja sem
 * grade nenhuma não conseguiria cadastrar produto.
 *
 * A validação daqui é para o erro aparecer no passo em que a pessoa digitou. A
 * validação que vale é a do servidor — e quando ela recusa um campo, o wizard
 * volta ao passo daquele campo.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Faixa, Pill } from '../../componentes/Basicos'
import { Botao } from '../../componentes/Botao'
import { Caixa, Campo, CampoSenha } from '../../componentes/Campos'
import { PainelLateral } from '../../componentes/PainelLateral'
import { api, ErroApi } from '../../plataforma/api'
import { cnpjValido, limparCNPJ, mascararCNPJ, somenteDigitos } from '../../plataforma/mascaras'

const CANAIS = ['Loja física', 'E-commerce', 'Mercado Livre', 'Shopee']

const PASSOS = [
  {
    titulo: 'Dados da loja',
    dica: 'Nome, CNPJ e canais',
    sub: 'Como sua loja aparece nas notas e no sistema.',
  },
  {
    titulo: 'Seu acesso',
    dica: 'Usuário administrador',
    sub: 'Este será o usuário administrador da conta.',
  },
  {
    titulo: 'Revisão',
    dica: 'Confirmar e criar',
    sub: 'Confira antes de criar a conta da loja.',
  },
]

export function TelaCriarConta() {
  const navegar = useNavigate()

  const [passo, setPasso] = useState(0)
  const [erro, setErro] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [criada, setCriada] = useState(false)

  // Passo 1 — dados da loja
  const [nomeLoja, setNomeLoja] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [filiais, setFiliais] = useState('')
  const [canais, setCanais] = useState<string[]>(['Loja física'])

  // Passo 2 — acesso
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')

  // Passo 3 — revisão
  const [termos, setTermos] = useState(false)

  function alternarCanal(canal: string) {
    setErro('')
    setCanais((atual) =>
      atual.includes(canal) ? atual.filter((c) => c !== canal) : [...atual, canal],
    )
  }

  function validarPasso(): string | null {
    if (passo === 0) {
      if (!nomeLoja.trim()) return 'Informe o nome da loja.'
      if (limparCNPJ(cnpj).length < 14) return 'Informe um CNPJ completo.'
      // Confere o dígito verificador aqui para o erro aparecer no campo em que
      // a pessoa digitou, e não no passo 3.
      if (!cnpjValido(cnpj)) return 'Informe um CNPJ válido.'
      if (canais.length === 0) return 'Selecione ao menos um canal de venda.'
    }
    if (passo === 1) {
      if (!nome.trim()) return 'Informe seu nome.'
      if (!email.includes('@')) return 'Informe um e-mail válido.'
      if (senha.length < 8) return 'A senha precisa de 8 caracteres ou mais.'
    }
    if (passo === 2 && !termos) return 'Aceite os termos para criar a conta.'
    return null
  }

  function avancar() {
    const problema = validarPasso()
    if (problema) {
      setErro(problema)
      return
    }
    setErro('')

    if (passo < PASSOS.length - 1) {
      setPasso(passo + 1)
      return
    }
    void criarConta()
  }

  async function criarConta() {
    setEnviando(true)
    setErro('')

    const corpo = {
      loja: {
        nome: nomeLoja.trim(),
        cnpj: limparCNPJ(cnpj),
        filiais: Number(filiais) || 1,
        canais,
      },
      administrador: { nome: nome.trim(), email: email.trim(), senha },
      // Grade vazia: o backend aplica a padrão. A loja ajusta em Configurações.
      grade: { roupas: [], numeracoes: [] },
      aceitou_termos: termos,
    }

    try {
      await api.post('/contas', corpo)
      setCriada(true)

      // A tela de login assume a view "Conta criada", como no handoff: quem
      // criou a conta precisa confirmar o e-mail antes de liberar o fiscal.
      setTimeout(() => navegar('/entrar', { state: { contaCriada: email.trim() } }), 900)
    } catch (problema) {
      setErro(mensagemDe(problema))
      voltarAoPassoDoCampo(problema)
    } finally {
      setEnviando(false)
    }
  }

  /**
   * Leva o wizard ao passo do campo recusado pelo servidor.
   *
   * Sem isso, um "e-mail já em uso" apareceria na revisão e o usuário teria que
   * descobrir sozinho que precisa voltar ao passo 2.
   */
  function voltarAoPassoDoCampo(problema: unknown) {
    if (!(problema instanceof ErroApi)) return

    const campo = Object.keys(problema.campos)[0] ?? ''
    if (campo.startsWith('loja.')) setPasso(0)
    else if (campo.startsWith('administrador.')) setPasso(1)
  }

  const resumo = [
    { rotulo: 'Loja', valor: nomeLoja || '—' },
    { rotulo: 'CNPJ', valor: cnpj || '—' },
    { rotulo: 'Filiais', valor: filiais || '1' },
    { rotulo: 'Canais', valor: canais.length ? canais.join(', ') : '—' },
    { rotulo: 'Administrador', valor: nome ? `${nome} · ${email}` : '—' },
  ]

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.06fr_1fr]">
      <PainelLateral
        titulo="Criar a conta da sua loja."
        subtitulo="Três passos e a loja está no ar. Estoque, produtos e equipe você configura depois, já dentro do sistema."
      >
        <ol className="flex flex-col gap-[2px]">
          {PASSOS.map((item, indice) => {
            const concluido = indice < passo
            const atual = indice === passo

            return (
              <li key={item.titulo}>
                <button
                  type="button"
                  disabled={!concluido}
                  onClick={() => {
                    setPasso(indice)
                    setErro('')
                  }}
                  aria-current={atual ? 'step' : undefined}
                  className={`flex w-full items-center gap-3 rounded-auth px-3 py-[9px] text-left ${
                    atual ? 'bg-marca-fundo' : ''
                  } ${concluido ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span
                    className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[12.5px] font-bold ${
                      concluido
                        ? 'bg-marca-suave text-marca'
                        : atual
                          ? 'bg-marca text-white'
                          : 'bg-divisoria text-texto-fraco'
                    }`}
                  >
                    {concluido ? '✓' : indice + 1}
                  </span>
                  <span>
                    <span
                      className={`block text-sm font-semibold ${
                        atual || concluido ? 'text-titulo' : 'text-texto-fraco'
                      }`}
                    >
                      {item.titulo}
                    </span>
                    <span className="block text-[12.5px] text-texto-fraco">{item.dica}</span>
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
      </PainelLateral>

      <div className="flex items-center justify-center px-10 py-16">
        <div className="w-full max-w-[436px]">
          <div className="mb-[18px] flex gap-2">
            {PASSOS.map((item, indice) => (
              <span
                key={item.titulo}
                className={`h-1 flex-1 rounded-full ${indice <= passo ? 'bg-marca' : 'bg-borda'}`}
              />
            ))}
          </div>

          <p className="mb-2 text-[12.5px] font-semibold tracking-[0.5px] text-marca">
            PASSO {passo + 1} DE {PASSOS.length}
          </p>
          <h1 className="mb-2 font-titulo text-[26px] font-bold text-titulo">
            {PASSOS[passo]?.titulo}
          </h1>
          <p className="mb-[26px] text-sm text-texto-suave">{PASSOS[passo]?.sub}</p>

          {passo === 0 && (
            <div className="flex flex-col gap-[18px]">
              <Campo
                variante="auth"
                rotulo="Nome da loja"
                placeholder="Cross Store Centro"
                autoComplete="organization"
                value={nomeLoja}
                onChange={(e) => {
                  setNomeLoja(e.target.value)
                  setErro('')
                }}
              />

              <div className="grid grid-cols-[1.2fr_1fr] gap-3">
                <Campo
                  variante="auth"
                  rotulo="CNPJ"
                  placeholder="00.000.000/0000-00"
                  inputMode="text"
                  // A máscara é aplicada a cada tecla; o que vai para a API é o
                  // valor cru, sem pontuação.
                  value={cnpj}
                  onChange={(e) => {
                    setCnpj(mascararCNPJ(e.target.value))
                    setErro('')
                  }}
                />
                <Campo
                  variante="auth"
                  rotulo="Filiais"
                  inputMode="numeric"
                  placeholder="1"
                  value={filiais}
                  onChange={(e) => setFiliais(somenteDigitos(e.target.value).slice(0, 3))}
                />
              </div>

              <div>
                <p className="mb-2 text-[13px] font-semibold text-texto">Canais de venda</p>
                <div className="flex flex-wrap gap-2">
                  {CANAIS.map((canal) => (
                    <Pill
                      key={canal}
                      ativa={canais.includes(canal)}
                      aoClicar={() => alternarCanal(canal)}
                    >
                      {canal}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {passo === 1 && (
            <div className="flex flex-col gap-[18px]">
              <Campo
                variante="auth"
                rotulo="Seu nome"
                autoComplete="name"
                placeholder="Ana Ribeiro"
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value)
                  setErro('')
                }}
              />
              <Campo
                variante="auth"
                rotulo="E-mail de acesso"
                type="email"
                autoComplete="username"
                placeholder="nome@sualoja.com.br"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setErro('')
                }}
              />
              <CampoSenha
                variante="auth"
                rotulo="Senha"
                autoComplete="new-password"
                placeholder="••••••••"
                comMedidor
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value)
                  setErro('')
                }}
              />
            </div>
          )}

          {passo === 2 && (
            <div className="flex flex-col gap-5">
              <dl className="divide-y divide-divisoria rounded-auth border border-borda">
                {resumo.map((linha) => (
                  <div key={linha.rotulo} className="flex justify-between gap-4 px-4 py-3">
                    <dt className="text-[13px] text-texto-suave">{linha.rotulo}</dt>
                    <dd className="text-right text-[13.5px] font-semibold text-titulo">
                      {linha.valor}
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="rounded-auth bg-marca-fundo px-4 py-3 text-[12.5px] leading-[1.6] text-texto-suave">
                Sua loja nasce com a grade padrão de tamanhos (P, M, G, GG e numeração 38 a 42). Dá
                para ajustar em Configurações, junto com produtos e equipe.
              </p>

              <Caixa marcada={termos} aoMudar={setTermos} tamanho={19}>
                Li e aceito os <span className="font-semibold text-marca">Termos de uso</span> e a{' '}
                <span className="font-semibold text-marca">Política de privacidade</span>.
              </Caixa>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {passo > 0 && !criada && (
              <Botao variante="secundario" porte="auth" altura={50} onClick={() => setPasso(passo - 1)}>
                Voltar
              </Botao>
            )}
            <div className="flex-1">
              <Botao
                porte="auth"
                altura={50}
                larguraTotal
                variante={criada ? 'sucesso' : 'primario'}
                disabled={criada || enviando}
                onClick={avancar}
              >
                {criada
                  ? 'Conta criada'
                  : enviando
                    ? 'Criando…'
                    : passo === PASSOS.length - 1
                      ? 'Criar conta'
                      : 'Continuar'}
              </Botao>
            </div>
          </div>

          {criada && (
            <Faixa tipo="sucesso">Conta criada. Enviamos a confirmação por e-mail.</Faixa>
          )}
          {erro && <Faixa tipo="erro">{erro}</Faixa>}

          <p className="mt-6 text-[13.5px] text-texto-suave">
            Já tem conta?{' '}
            <Link to="/entrar" className="font-semibold text-marca hover:text-marca-forte">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

function mensagemDe(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message
  return 'Não foi possível criar a conta. Tente novamente.'
}
